import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, DatePicker, Input, InputNumber, Modal, Row, Space, Tag, Typography, message, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LeftOutlined, PlusOutlined, SaveOutlined, DeleteOutlined, SwapRightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { fetchSqlData, insertData, updateData, deleteData } from '../../../services/api';
import { MatGapIssueRequest, MatGapIssueRequestItem, VwMatGapWhInventory } from '../../../types';
import { useGetMatGapWhInventory, useGetMatWarehouses } from '../../../hooks/genericHooks';
import MatGapMaterialSelect from '../../../components/modals/MatGapMaterialSelect';
import StandardTable from '../../../components/common/StandardTable';
import { useAuth } from '../../../components/common/security/AuthContext';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

interface EditableItem extends MatGapIssueRequestItem {
  key: string;
  _status?: 'unchanged' | 'updated' | 'new' | 'deleted';
  description?: string | null;
}

export default function MatGapIssueRequestEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const passedRefNo = (location.state as any)?.ref_no as string | undefined;
  const { user: username } = useAuth();

  const [header, setHeader] = useState<MatGapIssueRequest | null>(null);
  const [refNo, setRefNo] = useState<string | null>(passedRefNo || null);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<EditableItem[]>([]);
  // Reusable material selection modal visibility
  const [addOpen, setAddOpen] = useState<boolean>(false);
  // Editable status
  const [status, setStatus] = useState<string>('');
  const currentStatus = (status || header?.status || '');
  const hasItemChanges = useMemo(() => items.some(it => it._status && it._status !== 'unchanged' && it._status !== undefined), [items]);
  const hasHeaderChanges = useMemo(() => !!header && (((header.notes || '') !== (notes || '')) || ((header.status || '') !== (status || ''))), [header, notes, status]);
  // Can modify rows/notes only while current status is pending
  const canEditItems = currentStatus === 'pending';
  // Allow saving if there are any changes, even if user just changed status away from pending (unsaved)
  const canSave = hasItemChanges || hasHeaderChanges;
  // Status transitions: allow changes until terminal states (issued, cancelled)
  const terminalStatuses = new Set(['issued', 'cancelled']);
  const canChangeStatus = !terminalStatuses.has((header?.status || '').toLowerCase());

  const allowedNextStatuses = useMemo(() => {
    const cur = (currentStatus || '').toLowerCase();
    switch (cur) {
      case 'pending':
        return ['approved', 'rejected', 'cancelled'];
      case 'approved':
        // After approved, only 'cancelled' can be selected manually.
        return ['cancelled'];
      case 'partially_issued':
        // Updated externally; do not allow manual changes.
        return [];
      case 'rejected':
        return ['pending', 'cancelled'];
      case 'issued':
      case 'cancelled':
        return [];
      default:
        return ['pending', 'approved', 'partially_issued', 'issued', 'rejected', 'cancelled'];
    }
  }, [currentStatus]);

  // inventory for adding/enriching items
  const { data: invData = [], isLoading: invLoading } = useGetMatGapWhInventory() as any;
  const inventory: VwMatGapWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const invByMatId = useMemo(() => {
    const m = new Map<string, VwMatGapWhInventory>();
    (inventory || []).forEach(r => { const k = String(r.mat_id); if (!m.has(k)) m.set(k, r); });
    return m;
  }, [inventory]);

  // warehouses map for displaying warehouse name
  const { data: whData = [] } = useGetMatWarehouses() as any;
  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    const list = Array.isArray(whData) ? whData : (whData?.data ?? []);
    (list || []).forEach((w: any) => { if (w?.id != null) m.set(w.id, w.name); });
    return m;
  }, [whData]);
  // warehouses owners map for authorization (owners are usernames)
  const whOwnersMap = useMemo(() => {
    const m = new Map<number, string[]>();
    const list = Array.isArray(whData) ? whData : (whData?.data ?? []);
    (list || []).forEach((w: any) => {
      if (w?.id != null) m.set(w.id, Array.isArray(w?.owners) ? w.owners : []);
    });
    return m;
  }, [whData]);

  // Load header by id (if needed) and then items by ref_no
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        let effectiveRef = refNo;
        if (!effectiveRef) {
          const hdrResp = await fetchSqlData('mat_gap_issue_requests', 'id', Number(id));
          const rows: MatGapIssueRequest[] = Array.isArray(hdrResp) ? hdrResp : (hdrResp?.data ?? []);
          const hdr = rows?.[0] || null;
          if (!hdr) { message.error('Request header not found'); return; }
          setHeader(hdr);
          setRefNo(hdr.ref_no);
          setNotes(hdr.notes || '');
          setStatus(hdr.status || '');
          effectiveRef = hdr.ref_no;
        } else {
          if (!header) {
            const hdrResp = await fetchSqlData('mat_gap_issue_requests', 'id', Number(id));
            const rows: MatGapIssueRequest[] = Array.isArray(hdrResp) ? hdrResp : (hdrResp?.data ?? []);
            const hdr = rows?.[0] || null;
            if (hdr) { setHeader(hdr); setNotes(hdr.notes || ''); setStatus(hdr.status || ''); }
          }
        }
        if (effectiveRef) {
          const itResp = await fetchSqlData('mat_gap_issue_request_items', 'ref_no', effectiveRef);
          const itRows: MatGapIssueRequestItem[] = Array.isArray(itResp) ? itResp : (itResp?.data ?? []);
          const withKeys: EditableItem[] = (itRows || []).map(r => ({ ...r, key: String(r.id), _status: 'unchanged' }));
          setItems(withKeys);
        }
      } catch (err) {
        console.error(err);
        message.error('Failed to load request');
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const statusTag = (st?: string) => {
    const map: Record<string, { color: string; label: string }> = {
      pending: { color: 'default', label: 'Pending' },
      approved: { color: 'blue', label: 'Approved' },
      partially_issued: { color: 'gold', label: 'Partially Issued' },
      issued: { color: 'green', label: 'Issued' },
      rejected: { color: 'red', label: 'Rejected' },
      cancelled: { color: 'volcano', label: 'Cancelled' },
    };
    const cfg = (st && map[st]) || { color: 'default', label: st || '-' };
    return <Tag color={cfg.color}>{cfg.label}</Tag>;
  };

  const setQty = (rowKey: string, qty: number) => {
    setItems(prev => prev.map(it => it.key === rowKey ? { ...it, requested_qty: qty, _status: it._status === 'new' ? 'new' : 'updated' } : it));
  };

  const removeItem = (rowKey: string) => {
    setItems(prev => prev.map(it => it.key === rowKey ? { ...it, _status: it._status === 'new' ? 'deleted' : 'deleted' } : it));
  };

  const visibleItems = useMemo(() => items.filter(it => it._status !== 'deleted'), [items]);

  // Distinct warehouses across visible items
  const distinctWhSet = useMemo(() => {
    const s = new Set<number>();
    visibleItems.forEach(it => { if (it.warehouse_id != null) s.add(it.warehouse_id); });
    return s;
  }, [visibleItems]);

  // Only enable Issue when persisted status is approved (not just local selection)
  const canIssue = useMemo(() => ((header?.status || '').toLowerCase() === 'approved'), [header?.status]);

  const goToIssuePage = () => {
    if (!refNo) { message.warning('Reference number not loaded'); return; }
    const onlyWh = (distinctWhSet.size === 1) ? Array.from(distinctWhSet)[0] : undefined;
    navigate('/material/gap-material/operations/issue', {
      state: { ref_no: refNo, from_warehouse_id: onlyWh }
    });
  };

  // Authorization: only warehouse owners can approve/reject
  const canApproveReject = useMemo(() => {
    const whIds = new Set<number>();
    visibleItems.forEach(it => { if (it.warehouse_id != null) whIds.add(it.warehouse_id); });
    const widArr = Array.from(whIds);
    if (widArr.length === 0) return false; // no warehouse specified -> cannot approve/reject
    if (!username) return false;
    return widArr.every(wid => {
      const owners = whOwnersMap.get(wid) || [];
      return owners.includes(username);
    });
  }, [visibleItems, whOwnersMap, username]);

  // Filter out approve/reject if user is not authorized
  const filteredNextStatuses = useMemo(() => {
    if (canApproveReject) return allowedNextStatuses;
    return allowedNextStatuses.filter(s => s !== 'approved' && s !== 'rejected');
  }, [allowedNextStatuses, canApproveReject]);

  const columns: ColumnsType<EditableItem> = [
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 120 },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true, render: (_, row) => invByMatId.get(String(row.mat_id))?.description ?? row.description ?? '' },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: 'Requested Qty', dataIndex: 'requested_qty', key: 'requested_qty', width: 160, render: (_, row) => (
      <InputNumber min={0} disabled={!canEditItems} value={row.requested_qty} onChange={(v) => setQty(row.key, typeof v === 'number' ? v : 0)} style={{ width: 140 }} />
    ) },
    { title: 'Warehouse', dataIndex: 'warehouse_id', key: 'warehouse_id', width: 160, render: (v) => whMap.get(v as number) || v },
    { title: 'Actions', key: 'actions', width: 100, render: (_, row) => (
      <Button action="delete" danger size="small" icon={<DeleteOutlined />} disabled={!canEditItems} onClick={() => removeItem(row.key)}>Delete</Button>
    )},
  ];

  const saveChanges = async () => {
    try {
      if (!refNo || !header) { message.warning('Header not loaded'); return; }
      setLoading(true);
      // Update header fields (notes/status) if changed
      const updatePayload: any = {};
      const willApprove = ((header.status || '').toLowerCase() !== 'approved') && ((status || '').toLowerCase() === 'approved');
      if ((header.notes || '') !== (notes || '')) updatePayload.notes = notes;
      if ((header.status || '') !== (status || '')) {
        // Guard: only owners can set approved/rejected
        const next = (status || '').toLowerCase();
        if ((next === 'approved' || next === 'rejected') && !canApproveReject) {
          message.error('Only warehouse owners can approve or reject this request.');
          setLoading(false);
          return;
        }
        updatePayload.status = status;
      }
      if (Object.keys(updatePayload).length > 0) {
        await updateData('mat_gap_issue_requests', header.id, updatePayload);
      }
      // Process deletions
      const deletions = items.filter(it => it._status === 'deleted' && it.id);
      for (const d of deletions) {
        await deleteData('mat_gap_issue_request_items', 'id', d.id);
      }
      // Process updates
      const updates = items.filter(it => it._status === 'updated' && it.id);
      for (const u of updates) {
        await updateData('mat_gap_issue_request_items', u.id, { requested_qty: u.requested_qty, notes: u.notes ?? null, warehouse_id: u.warehouse_id ?? null });
      }
      // Process new items
      const news = items.filter(it => it._status === 'new');
      if (news.length > 0) {
        const payload = news.map(n => ({
          ref_no: refNo,
          mat_id: n.mat_id,
          unit: n.unit,
          requested_qty: n.requested_qty,
          warehouse_id: n.warehouse_id ?? null,
          notes: n.notes ?? null,
        }));
        await insertData('mat_gap_issue_request_items', payload);
      }
      message.success('Changes saved');
      // If this save transitioned status to approved, go directly to Issue page
      if (willApprove) {
        const onlyWh = (distinctWhSet.size === 1) ? Array.from(distinctWhSet)[0] : undefined;
        navigate('/material/gap-material/operations/issue', { state: { ref_no: refNo, from_warehouse_id: onlyWh } });
      } else {
        // Otherwise, go back and trigger list refresh
        try { sessionStorage.setItem('refresh_mat_issue_requests', '1'); } catch {}
        navigate(-1);
      }
    } catch (err) {
      console.error(err);
      message.error('Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const addSelectedFromModal = (rows: { mat_id: string; description: string; unit: string; prod_group: string; ext_group: string; hierarchy_long: string; warehouse_id: number; onhand_qty: number; }[]) => {
    if (!refNo) { message.warning('Header not loaded'); return; }
    const additions: EditableItem[] = [];
    rows.forEach(row => {
      // If already exists with same mat_id and warehouse, skip or update
      const existing = items.find(it => it.mat_id === row.mat_id && (it.warehouse_id || row.warehouse_id) === row.warehouse_id && it._status !== 'deleted');
      if (existing) return;
      additions.push({
        id: 0 as any,
        ref_no: refNo,
        mat_id: row.mat_id,
        unit: row.unit,
        requested_qty: 0,
        notes: row.description || null,
        warehouse_id: row.warehouse_id,
        key: `new-${row.mat_id}-${row.warehouse_id}-${Date.now()}`,
        _status: 'new',
        description: row.description,
      });
    });
    if (additions.length > 0) setItems(prev => [...prev, ...additions]);
    setAddOpen(false);
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }} loading={loading}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Edit Issue Request</Title>
              <Text type="secondary">Modify items and quantities, add or delete lines</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button action="edit" type="primary" icon={<SaveOutlined />} onClick={saveChanges} disabled={!canSave}>Save Changes</Button>
            <Button action="edit" icon={<PlusOutlined />} onClick={() => setAddOpen(true)} disabled={!canEditItems}>Add Items</Button>
            <Button action="add" icon={<SwapRightOutlined />} onClick={goToIssuePage} disabled={!canIssue}>Issue</Button>
          </Space>
        </Col>
      </Row>

      {/* Header info */}
      <Row style={{ marginBottom: 8 }}>
        <Col span={24}>
          <Space size={12} wrap>
            <Text><b>Ref:</b> {refNo || '-'}</Text>
            <Text><b>Date:</b> {header?.req_date ? dayjs(header.req_date).format('YYYY-MM-DD') : '-'}</Text>
            <Space>
              <Text><b>Status:</b></Text>
              <Select
                value={currentStatus || undefined}
                onChange={(v) => setStatus(v || '')}
                style={{ width: 200 }}
                disabled={!canChangeStatus}
                options={(() => {
                  const opts = new Set<string>(filteredNextStatuses);
                  if (currentStatus) opts.add(currentStatus);
                  return Array.from(opts).map(v => ({ value: v, label: v }));
                })()}
              />
              {!canApproveReject && (
                <Text type="secondary">Only warehouse owners can set Approved/Rejected</Text>
              )}
              {status ? statusTag(status) : null}
            </Space>
            <Text><b>Requester:</b> {header?.requester} - {header?.requester_name}</Text>
          </Space>
        </Col>
      </Row>
      <Row style={{ marginBottom: 12 }}>
        <Col span={24}>
          <Input.TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" disabled={!canEditItems} />
        </Col>
      </Row>

      {/* Items table (StandardTable for consistency) */}
      <StandardTable
        columns={columns as any}
        dataSource={visibleItems}
        rowKey={(r: any) => r.key}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
      />

      {/* Reusable Material Selection Modal */}
      <MatGapMaterialSelect
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={addSelectedFromModal}
        title="Add Items from Inventory"
      />
    </Card>
  );
}
